/**
 * Stack detection (ADR 0046 point 6, R-PL-1): a pure function turns a repo's file list and the
 * text of a few small files into a StackProfile, so missions can verify work in any language
 * and the setup plan can propose commands. It runs on a machine (agent module `stack`,
 * read-only, at most once an hour and on HEAD change) and on the server over a provider's API.
 *
 * Rules are data (the tables below), so the Go agent can port them one to one: lockfiles and
 * manifests name the language and package manager, task runners and manifest scripts declare
 * commands, CI files show the commands CI runs, and each ecosystem has a conventional default.
 * Declared wins over ci, which wins over conventional.
 *
 * Repo content is untrusted: a detected command is shown verbatim (a declared script's text in
 * `runs`), saved only by a person or an approved setup plan, and run only as job.run under
 * policy. Detection itself never runs, evaluates or fetches anything. Browser-safe.
 */
import type { Id, Ts } from '../index.ts';
import { CI_PROVIDER_IDS } from './ci.ts';
import { scopedTestLine, testScopeFor } from './test-scope.ts';
import type { CiDetection, CiProviderId } from './ci.ts';
import {
  isYamlMap,
  jget,
  jkeys,
  jstr,
  justBody,
  justRecipes,
  makeRecipe,
  makeTargets,
  parseJson,
  parseToml,
  parseYaml,
  tomlHas,
  tomlString,
  tomlText,
  yget,
  ylist,
  ystr,
} from './stack-parse.ts';
import type { TomlDoc, YamlValue } from './stack-parse.ts';

export type StackCommandKind = 'install' | 'build' | 'test' | 'lint' | 'typecheck' | 'format';
export const STACK_COMMAND_KINDS: readonly StackCommandKind[] = ['install', 'build', 'test', 'lint', 'typecheck', 'format'];

/** A script says so, a CI step runs it, or the ecosystem's default. Declared wins over ci over conventional. */
export type StackConfidence = 'declared' | 'ci' | 'conventional';
export const STACK_CONFIDENCE_ORDER: readonly StackConfidence[] = ['declared', 'ci', 'conventional'];

export interface StackCommand {
  command: string;
  confidence: StackConfidence;
  /** "package.json scripts.test", ".github/workflows/ci.yml job 'test' step 'Test'". */
  evidence: string;
  /**
   * What a declared command runs, verbatim from the repo, so a person sees what saving it would
   * run: the script with its pre and post scripts, or every rule of a make target with its
   * prerequisites line, or a just recipe or task with its dependencies. Control, invisible and
   * bidirectional characters are shown as `\u{...}` escapes. A task runner also evaluates the
   * rest of its file (make variables, SHELL), which is not shown. Absent when the command is the
   * text itself (ci, conventional).
   */
  runs?: string;
}

export interface StackEvidence {
  /** 'pnpm', 'turbo', 'node'. */
  id: string;
  evidence: string;
}

export interface StackProfile {
  /** 'typescript', 'go', 'python', 'rust', 'java', 'csharp', ... */
  languages: string[];
  /** 'react', 'next', 'django', 'spring', ... when a manifest names one. */
  frameworks: string[];
  /** 'pnpm' from pnpm-lock.yaml. */
  packageManagers: StackEvidence[];
  /** turbo.json, nx.json, pnpm-workspace.yaml, MODULE.bazel. */
  monorepo?: { tool: string; evidence: string };
  /** .nvmrc, .tool-versions, rust-toolchain, the go line of go.mod. */
  toolchains: { tool: string; version: string; evidence: string }[];
  commands: Partial<Record<StackCommandKind, StackCommand>>;
  /** From the CI files found (every provider Orgus connects to). */
  ci: CiDetection[];
  /** wrangler.toml, vercel.json, netlify.toml, fly.toml, Dockerfile, k8s, serverless.yml, Procfile. */
  deployHints: { target: string; evidence: string }[];
  /** Things the rules could not tell, each a question a person can answer ("Which command runs the tests?"). */
  unknowns: string[];
  /**
   * What a landing verifies a branch with, when the repo declares a script that tests only what
   * a branch changes (package.json scripts.test:affected). Landing settings suggest it before the
   * test command (`suggestedVerifyCommand`).
   */
  verify?: StackCommand;
}

/** What detection reads: root and well-known paths, and the text of a few small files by path. */
export interface StackInput {
  files: readonly string[];
  texts?: Readonly<Record<string, string>>;
}

export function emptyStackProfile(): StackProfile {
  return { languages: [], frameworks: [], packageManagers: [], toolchains: [], commands: {}, ci: [], deployHints: [], unknowns: [] };
}

// ---------------------------------------------------------------------------
// What detection reads (the fixed list; the agent reads nothing else)
// ---------------------------------------------------------------------------

/** A file larger than this is listed but its text is not read. */
export const STACK_MAX_TEXT_BYTES = 64 * 1024;
/** Paths detection looks at; a longer list is cut (a server-side tree can be large). */
export const STACK_MAX_FILES = 5000;
/** CI files read per folder (.github/workflows can hold many). */
export const STACK_MAX_CI_FILES = 40;
/** Root .NET project files (.csproj, .fsproj) read, by name order; more are listed but not read. */
export const STACK_MAX_PROJECT_FILES = 16;

/** Root files whose text detection reads, when present. */
export const STACK_TEXT_FILES: readonly string[] = [
  // JavaScript and TypeScript
  'package.json',
  'deno.json',
  'deno.jsonc',
  '.nvmrc',
  '.node-version',
  '.tool-versions',
  // Rust, Go
  'Cargo.toml',
  'rust-toolchain',
  'rust-toolchain.toml',
  'go.mod',
  // Python
  'pyproject.toml',
  'requirements.txt',
  'Pipfile',
  '.python-version',
  // Ruby, PHP, JVM, .NET
  'Gemfile',
  '.ruby-version',
  'composer.json',
  'pom.xml',
  'build.gradle',
  'build.gradle.kts',
  '.java-version',
  'global.json',
  // Elixir, Dart, Swift, C and C++
  'mix.exs',
  'pubspec.yaml',
  'Package.swift',
  'CMakeLists.txt',
  // Task runners
  'Makefile',
  'makefile',
  'GNUmakefile',
  'justfile',
  'Justfile',
  '.justfile',
  'Taskfile.yml',
  'Taskfile.yaml',
  // CI at the root
  '.gitlab-ci.yml',
  'azure-pipelines.yml',
  'azure-pipelines.yaml',
  'Jenkinsfile',
];

/** Folders whose entries detection lists, besides the root. Nothing deeper is listed. */
export const STACK_LIST_DIRS: readonly string[] = ['.github/workflows', '.circleci', '.buildkite'];

/** True when detection reads this path's text (a root file in the list, a CI file, a root .NET project). */
export function stackReadsText(path: string): boolean {
  if (STACK_TEXT_FILES.includes(path)) return true;
  if (/^\.github\/workflows\/[^/]+\.ya?ml$/.test(path)) return true;
  if (path === '.circleci/config.yml' || path === '.circleci/config.yaml') return true;
  if (/^\.buildkite\/pipeline\.ya?ml$/.test(path)) return true;
  return /^[^/]+\.(csproj|fsproj)$/.test(path);
}

// ---------------------------------------------------------------------------
// Rules tables
// ---------------------------------------------------------------------------

/** Lockfiles of the JavaScript package managers, in the order a tie is broken. */
export const NODE_LOCKFILES: readonly (readonly [file: string, pm: string])[] = [
  ['pnpm-lock.yaml', 'pnpm'],
  ['yarn.lock', 'yarn'],
  ['bun.lock', 'bun'],
  ['bun.lockb', 'bun'],
  // npm last: a stale package-lock.json is the most common leftover.
  ['package-lock.json', 'npm'],
  ['npm-shrinkwrap.json', 'npm'],
];

/** Manifest script names for each command kind, preferred first. */
export const SCRIPT_NAMES: Readonly<Record<StackCommandKind, readonly string[]>> = {
  install: [],
  build: ['build'],
  test: ['test'],
  lint: ['lint'],
  typecheck: ['typecheck', 'type-check', 'check-types', 'types', 'tsc'],
  format: ['format:check', 'fmt:check', 'check-format', 'format', 'fmt'],
};

/** Task runner target names for each command kind, preferred first. `make install` installs the software, so it is never read as install. */
export const TARGET_NAMES: Readonly<Record<StackCommandKind, readonly string[]>> = {
  install: ['deps', 'install-deps', 'setup', 'bootstrap'],
  build: ['build'],
  // 'check' is the GNU name for the test suite.
  test: ['test', 'tests', 'check'],
  lint: ['lint'],
  typecheck: ['typecheck', 'type-check'],
  format: ['fmt-check', 'format-check', 'check-format', 'fmt', 'format'],
};

/** Dependency names that reveal a framework, in the order frameworks are listed. */
export const NODE_FRAMEWORKS: readonly (readonly [dep: string, framework: string])[] = [
  ['next', 'next'],
  ['nuxt', 'nuxt'],
  ['@sveltejs/kit', 'sveltekit'],
  ['svelte', 'svelte'],
  ['astro', 'astro'],
  ['@remix-run/react', 'remix'],
  ['@angular/core', 'angular'],
  ['vue', 'vue'],
  ['react-native', 'react-native'],
  ['expo', 'expo'],
  ['react', 'react'],
  ['@nestjs/core', 'nestjs'],
  ['express', 'express'],
  ['fastify', 'fastify'],
  ['hono', 'hono'],
  ['electron', 'electron'],
];

export const PYTHON_FRAMEWORKS: readonly string[] = ['django', 'flask', 'fastapi'];

/** Files and root folders (trailing slash) that hint where the project deploys. */
export const DEPLOY_HINTS: readonly (readonly [path: string, target: string])[] = [
  ['wrangler.toml', 'cloudflare-workers'],
  ['wrangler.json', 'cloudflare-workers'],
  ['wrangler.jsonc', 'cloudflare-workers'],
  ['vercel.json', 'vercel'],
  ['netlify.toml', 'netlify'],
  ['fly.toml', 'fly'],
  ['render.yaml', 'render'],
  ['app.yaml', 'google-app-engine'],
  ['firebase.json', 'firebase'],
  ['amplify.yml', 'aws-amplify'],
  ['cdk.json', 'aws-cdk'],
  ['serverless.yml', 'serverless'],
  ['serverless.yaml', 'serverless'],
  ['Procfile', 'heroku'],
  ['Dockerfile', 'docker'],
  ['docker-compose.yml', 'docker-compose'],
  ['docker-compose.yaml', 'docker-compose'],
  ['compose.yml', 'docker-compose'],
  ['compose.yaml', 'docker-compose'],
  ['Chart.yaml', 'helm'],
  ['charts/', 'helm'],
  ['kustomization.yaml', 'kubernetes'],
  ['skaffold.yaml', 'kubernetes'],
  ['k8s/', 'kubernetes'],
  ['kubernetes/', 'kubernetes'],
  ['Pulumi.yaml', 'pulumi'],
  ['main.tf', 'terraform'],
  ['terraform/', 'terraform'],
];

/** Monorepo tools, the first found wins (orchestrators before workspace managers). */
export const MONOREPO_FILES: readonly (readonly [path: string, tool: string])[] = [
  ['turbo.json', 'turbo'],
  ['nx.json', 'nx'],
  ['lerna.json', 'lerna'],
  ['rush.json', 'rush'],
  ['MODULE.bazel', 'bazel'],
  ['WORKSPACE.bazel', 'bazel'],
  ['WORKSPACE', 'bazel'],
  ['pants.toml', 'pants'],
  ['pnpm-workspace.yaml', 'pnpm workspaces'],
  ['go.work', 'go workspaces'],
];

const NPM_PLACEHOLDER_TEST = /no test specified/i;

// ---------------------------------------------------------------------------
// CI command classification
// ---------------------------------------------------------------------------

/** First words of a CI step line that detection reads as a command; anything else is ignored. */
const CI_RUNNERS = new Set([
  'npm', 'pnpm', 'yarn', 'bun', 'npx', 'pnpx', 'bunx', 'deno', 'turbo', 'nx',
  'cargo', 'go', 'uv', 'poetry', 'pdm', 'hatch', 'pipenv', 'python', 'python3', 'pip', 'pip3', 'pytest', 'tox', 'nox',
  'bundle', 'rake', 'rails', 'bin/rails', 'composer', 'vendor/bin/phpunit', './vendor/bin/phpunit', 'vendor/bin/pest',
  'mvn', 'mvnw', './mvnw', 'gradle', 'gradlew', './gradlew', 'dotnet', 'mix', 'dart', 'flutter', 'swift',
  'cmake', 'ctest', 'make', 'just', 'task', 'ruff', 'mypy', 'pyright', 'tsc', 'eslint', 'golangci-lint', 'gofmt', 'rubocop',
]);

/** Runners that are a command kind by themselves. */
const CI_DIRECT: Readonly<Record<string, StackCommandKind>> = {
  pytest: 'test', tox: 'test', nox: 'test', ctest: 'test',
  'vendor/bin/phpunit': 'test', './vendor/bin/phpunit': 'test', 'vendor/bin/pest': 'test',
  ruff: 'lint', eslint: 'lint', 'golangci-lint': 'lint', rubocop: 'lint',
  mypy: 'typecheck', pyright: 'typecheck', tsc: 'typecheck', gofmt: 'format',
};

/** Words after the runner, in the order kinds are tried for one word. */
const CI_WORDS: readonly (readonly [StackCommandKind, readonly string[]])[] = [
  ['install', ['ci', 'install', 'i', 'sync', 'restore', 'deps.get', 'download', 'fetch', 'resolve', 'get']],
  ['typecheck', ['typecheck', 'type-check', 'check-types', 'tsc', 'mypy', 'pyright']],
  ['lint', ['lint', 'clippy', 'vet', 'golangci-lint', 'rubocop', 'flake8', 'pylint', 'eslint', 'credo', 'analyze']],
  ['format', ['fmt', 'format', 'prettier', 'gofmt']],
  ['test', ['test', 'tests', 'pytest', 'rspec', 'phpunit', 'verify', 'tox', 'nox', 'unittest', 'ctest']],
  ['build', ['build', 'compile', 'assemble', 'package']],
];

/**
 * Shell and CI syntax that makes a line more than one plain command: pipes, redirects, a lone `&`
 * (runs the next command in the background), backticks, `$( )` substitution, and CI template
 * expressions (`${{ }}`, `$[[ ]]`) that only mean something inside the CI system.
 */
const CI_UNSAFE = /[|`<>]|\$\(|\$\{\{|\$\[\[|(^|[^&])&(?!&)/;

/**
 * Control characters, and the invisible and bidirectional formatting characters that make text
 * read differently from what runs (Trojan Source).
 */
const INVISIBLE = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b-\u200f\u2028-\u202e\u2060-\u2069\ufeff]/;

/** Flags that move a command to another folder: like `cd`, the command depends on where it runs. */
const CI_DIR_FLAG = /^(-C|--cwd|--prefix|--dir|--directory|--working-directory|--manifest-path|--project-dir)(=|$)/;

/**
 * Which command kind one CI step command is, or null. Only single commands that start with a
 * known build tool count: pipes, background `&`, backticks, substitutions, CI template
 * expressions, invisible characters, folder-changing flags and leading variable assignments
 * (which may carry secrets) are never read as commands.
 */
export function classifyCiCommand(segment: string): StackCommandKind | null {
  const s = segment.trim();
  if (!s || s.length > 300 || CI_UNSAFE.test(s) || INVISIBLE.test(s)) return null;
  const tokens = s.split(/[ \t]+/);
  const runner = tokens[0];
  if (!CI_RUNNERS.has(runner)) return null;
  if (tokens.some((t) => CI_DIR_FLAG.test(t))) return null;
  const words = tokens
    .slice(1)
    .filter((t) => !t.startsWith('-'))
    .flatMap((t) => t.toLowerCase().split(':'));
  // A tool that is a kind by itself keeps it ('ctest --test-dir build' tests), except `ruff format`.
  const direct = Object.prototype.hasOwnProperty.call(CI_DIRECT, runner) ? CI_DIRECT[runner] : null;
  if (direct) return words.includes('format') ? 'format' : direct;
  for (const w of words) {
    if (w === 'check' && (runner === 'cargo' || runner === 'deno')) return 'typecheck';
    for (const [kind, list] of CI_WORDS) if (list.includes(w)) return kind;
  }
  return null;
}

/**
 * The commands one CI step runs, each with its kind. A line is read only when every command on
 * it (split on && and ;) is a known build tool: `cd app && npm test` depends on where it runs, so
 * it is skipped. A line whose commands are all one kind is kept whole
 * (`cmake -S . -B build && cmake --build build`); a mixed line gives each command alone.
 */
export function ciStepCommands(text: string): { command: string; kind: StackCommandKind }[] {
  const out: { command: string; kind: StackCommandKind }[] = [];
  for (const raw of text.split(/\r?\n/).slice(0, 200)) {
    const line = raw.trim();
    // A continued line is not one whole command.
    if (!line || line.startsWith('#') || line.endsWith('\\')) continue;
    const parts = line.split(/&&|;/).map((p) => p.trim()).filter(Boolean);
    const kinds = parts.map(classifyCiCommand);
    if (kinds.length === 0 || kinds.some((k) => k === null)) continue;
    if (kinds.every((k) => k === kinds[0])) out.push({ command: line, kind: kinds[0]! });
    else parts.forEach((p, i) => out.push({ command: p, kind: kinds[i]! }));
  }
  return out;
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

interface Candidate {
  kind: StackCommandKind;
  cmd: StackCommand;
  rank: number;
}

class Builder {
  readonly profile = emptyStackProfile();
  /** A workspace declared inside a manifest; used when no monorepo file says more. */
  monorepoCandidate: { tool: string; evidence: string } | undefined;
  private readonly candidates: Candidate[] = [];
  private readonly files: Set<string>;
  private readonly topDirs = new Set<string>();
  private readonly texts: Readonly<Record<string, string>>;

  constructor(input: StackInput) {
    const list = Array.isArray(input?.files) ? input.files.slice(0, STACK_MAX_FILES) : [];
    this.files = new Set();
    for (const raw of list) {
      if (typeof raw !== 'string') continue;
      const f = raw.replace(/\\/g, '/').replace(/^\.\//, '');
      this.files.add(f);
      const slash = f.indexOf('/');
      if (slash > 0) this.topDirs.add(f.slice(0, slash));
    }
    this.texts = input?.texts && typeof input.texts === 'object' ? input.texts : {};
  }

  has(path: string): boolean {
    if (path.endsWith('/')) return this.topDirs.has(path.slice(0, -1));
    return this.files.has(path);
  }

  /** Root files matching a pattern, sorted. */
  rootMatching(re: RegExp): string[] {
    return [...this.files].filter((f) => !f.includes('/') && re.test(f)).sort();
  }

  /** All listed paths under a folder, sorted. */
  under(dir: string, re: RegExp): string[] {
    return [...this.files].filter((f) => f.startsWith(`${dir}/`) && re.test(f.slice(dir.length + 1))).sort();
  }

  text(path: string): string | null {
    if (!this.files.has(path) || !Object.prototype.hasOwnProperty.call(this.texts, path)) return null;
    const t = this.texts[path];
    return typeof t === 'string' && t.length <= STACK_MAX_TEXT_BYTES ? t : null;
  }

  lang(id: string): void {
    if (!this.profile.languages.includes(id)) this.profile.languages.push(id);
  }
  framework(id: string): void {
    if (!this.profile.frameworks.includes(id)) this.profile.frameworks.push(id);
  }
  pm(id: string, evidence: string): void {
    if (!this.profile.packageManagers.some((p) => p.id === id)) this.profile.packageManagers.push({ id, evidence });
  }
  tool(tool: string, version: string | null | undefined, evidence: string): void {
    const v = (version ?? '').trim();
    if (!v || v.length > 80 || /[\u0000-\u001f]/.test(v)) return;
    if (!this.profile.toolchains.some((t) => t.tool === tool && t.version === v)) this.profile.toolchains.push({ tool, version: v, evidence });
  }
  monorepo(tool: string, evidence: string): void {
    if (!this.profile.monorepo) this.profile.monorepo = { tool, evidence };
  }
  unknown(q: string): void {
    if (!this.profile.unknowns.includes(q)) this.profile.unknowns.push(q);
  }

  offer(kind: StackCommandKind, cmd: StackCommand): void {
    this.candidates.push({ kind, cmd, rank: this.candidates.length });
  }

  finish(): StackProfile {
    const order = (c: StackConfidence) => STACK_CONFIDENCE_ORDER.indexOf(c);
    for (const kind of STACK_COMMAND_KINDS) {
      const best = this.candidates.filter((c) => c.kind === kind).sort((a, b) => order(a.cmd.confidence) - order(b.cmd.confidence) || a.rank - b.rank)[0];
      if (best) this.profile.commands[kind] = best.cmd;
    }
    if (this.profile.languages.length === 0) this.unknown('Which language and build tool does this project use? No manifest, lockfile or build file was found at the top of the repository.');
    if (!this.profile.commands.test) this.unknown('Which command runs the tests? Nothing in the repository declares one.');
    return this.profile;
  }
}

/**
 * Repo text made safe to show: every control, invisible or bidirectional formatting character
 * (except newline and tab) becomes a visible `\u{...}` escape, so what a person reads is what runs.
 */
export function stackVisibleText(s: string): string {
  return s.replace(new RegExp(INVISIBLE.source, 'g'), (c) => `\\u{${c.codePointAt(0)!.toString(16)}}`);
}

/** A repo-given name (job, pipeline, step) for display: invisible characters escaped, at most 200 characters. */
function label(s: string): string {
  const v = stackVisibleText(s);
  return v.length > 200 ? `${v.slice(0, 197)}...` : v;
}

function declared(b: Builder, kind: StackCommandKind, command: string, evidence: string, runs: string | null): void {
  b.offer(kind, runs ? { command, confidence: 'declared', evidence, runs: stackVisibleText(runs) } : { command, confidence: 'declared', evidence });
}

function conventional(b: Builder, kind: StackCommandKind, command: string, evidence: string): void {
  b.offer(kind, { command, confidence: 'conventional', evidence });
}

/** Offer every kind's conventional command, for one ecosystem. */
function conventions(b: Builder, evidence: string, cmds: Partial<Record<StackCommandKind, string | null | false>>): void {
  for (const kind of STACK_COMMAND_KINDS) {
    const c = cmds[kind];
    if (c) conventional(b, kind, c, evidence);
  }
}

/** A word in a dependency list (case-insensitive, not part of a longer name). */
function mentions(text: string, name: string): boolean {
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z0-9_.-])${esc}([^A-Za-z0-9_-]|$)`, 'im').test(text);
}

function firstLine(t: string | null): string | null {
  if (t === null) return null;
  const line = t.split(/\r?\n/).map((l) => l.trim()).find((l) => l && !l.startsWith('#'));
  return line ?? null;
}

// ---------------------------------------------------------------------------
// Task runners (declared, ranked first: the repo's own entry point)
// ---------------------------------------------------------------------------

function taskRunners(b: Builder): void {
  for (const file of ['Makefile', 'makefile', 'GNUmakefile']) {
    const src = b.text(file);
    if (src === null) continue;
    const targets = makeTargets(src);
    for (const kind of STACK_COMMAND_KINDS) {
      const t = TARGET_NAMES[kind].find((n) => targets.includes(n));
      if (t) declared(b, kind, `make ${t}`, `${file} target ${t}`, makeRecipe(src, t));
    }
    if (!targets.includes('build') && targets[0] === 'all') declared(b, 'build', 'make', `${file} target all`, makeRecipe(src, 'all'));
    break;
  }
  for (const file of ['justfile', 'Justfile', '.justfile']) {
    const src = b.text(file);
    if (src === null) continue;
    const recipes = justRecipes(src);
    for (const kind of STACK_COMMAND_KINDS) {
      const r = TARGET_NAMES[kind].find((n) => recipes.includes(n));
      if (r) declared(b, kind, `just ${r}`, `${file} recipe ${r}`, justBody(src, r));
    }
    break;
  }
  for (const file of ['Taskfile.yml', 'Taskfile.yaml']) {
    const src = b.text(file);
    if (src === null) continue;
    const tasks = yget(parseYaml(src), 'tasks');
    const names = isYamlMap(tasks) ? Object.keys(tasks) : [];
    for (const kind of STACK_COMMAND_KINDS) {
      const t = TARGET_NAMES[kind].find((n) => names.includes(n));
      if (!t) continue;
      const cmds = yget(yget(tasks, t), 'cmds');
      // Dependencies run first; a task call runs that task.
      const deps = ylist(yget(yget(tasks, t), 'deps')).map((d) => `task ${d}`);
      const steps = Array.isArray(cmds) ? cmds.map((c) => (typeof c === 'string' ? c : (ystr(yget(c, 'cmd')) ?? (ystr(yget(c, 'task')) !== null ? `task ${ystr(yget(c, 'task'))}` : '')))) : [];
      const runs = [...deps, ...steps].filter(Boolean).join('\n');
      declared(b, kind, `task ${t}`, `${file} task ${t}`, runs);
    }
    break;
  }
}

// ---------------------------------------------------------------------------
// Ecosystems
// ---------------------------------------------------------------------------

function nodeEcosystem(b: Builder): void {
  const hasPkg = b.has('package.json');
  const locks = NODE_LOCKFILES.filter(([f]) => b.has(f));
  if (!hasPkg && locks.length === 0) return;
  const pkgText = b.text('package.json');
  const pkg = pkgText === null ? null : parseJson(pkgText);
  const declaredPm = /^(npm|pnpm|yarn|bun)@([0-9][^\s+]*)/.exec(jstr(pkg, 'packageManager') ?? '');
  const lockPms = [...new Set(locks.map(([, pm]) => pm))];
  const primary = declaredPm?.[1] ?? lockPms[0] ?? 'npm';
  if (declaredPm) {
    b.pm(declaredPm[1], 'package.json packageManager');
    b.tool(declaredPm[1], declaredPm[2], 'package.json packageManager');
  }
  for (const [file, pm] of locks) b.pm(pm, file);
  if (!declaredPm && locks.length === 0) b.pm('npm', 'package.json (no lockfile)');
  if (!declaredPm && lockPms.length > 1) {
    const files = lockPms.map((pm) => locks.find(([, p]) => p === pm)![0]);
    b.unknown(`Lockfiles for ${lockPms.join(' and ')} are both present (${files.join(', ')}): which package manager does this project use?`);
  }

  const deps = [...jkeys(jget(pkg, 'dependencies')), ...jkeys(jget(pkg, 'devDependencies')), ...jkeys(jget(pkg, 'peerDependencies'))];
  b.lang(b.has('tsconfig.json') || deps.includes('typescript') ? 'typescript' : 'javascript');
  for (const [dep, fw] of NODE_FRAMEWORKS) if (deps.includes(dep)) b.framework(fw);

  b.tool('node', firstLine(b.text('.nvmrc')), '.nvmrc');
  b.tool('node', firstLine(b.text('.node-version')), '.node-version');
  b.tool('node', jstr(jget(pkg, 'engines'), 'node'), 'package.json engines.node');
  if (jget(pkg, 'workspaces') !== undefined) b.monorepoCandidate = { tool: `${primary} workspaces`, evidence: 'package.json workspaces' };

  const scripts = jget(pkg, 'scripts');
  for (const kind of STACK_COMMAND_KINDS) {
    for (const name of SCRIPT_NAMES[kind]) {
      const body = jstr(scripts, name);
      if (body === null) continue;
      if (kind === 'test' && NPM_PLACEHOLDER_TEST.test(body)) continue;
      // npm, yarn 1, pnpm and bun run the pre and post scripts around it: show them too.
      const runs = [jstr(scripts, `pre${name}`), body, jstr(scripts, `post${name}`)].filter((x): x is string => x !== null && x !== '').join('\n');
      declared(b, kind, name === 'test' ? `${primary} test` : `${primary} run ${name}`, `package.json scripts.${name}`, runs);
      break;
    }
  }
  // A landing verifies with the script that tests only what a branch changes, when there is one.
  const affected = jstr(scripts, 'test:affected');
  if (affected !== null && affected !== '' && !b.profile.verify) {
    const runs = [jstr(scripts, 'pretest:affected'), affected, jstr(scripts, 'posttest:affected')].filter((x): x is string => x !== null && x !== '').join('\n');
    b.profile.verify = { command: `${primary} run test:affected`, confidence: 'declared', evidence: 'package.json scripts.test:affected', runs: stackVisibleText(runs) };
  }
  const lock = locks.find(([, pm]) => pm === primary)?.[0];
  const berry = primary === 'yarn' && (b.has('.yarnrc.yml') || /^[2-9]/.test(declaredPm?.[1] === 'yarn' ? declaredPm[2] : ''));
  const install = !lock
    ? `${primary} install`
    : primary === 'npm'
      ? 'npm ci'
      : primary === 'pnpm'
        ? 'pnpm install --frozen-lockfile'
        : primary === 'yarn'
          ? berry
            ? 'yarn install --immutable'
            : 'yarn install --frozen-lockfile'
          : 'bun install --frozen-lockfile';
  conventional(b, 'install', install, lock ?? 'package.json');
}

function denoEcosystem(b: Builder): void {
  const file = b.has('deno.json') ? 'deno.json' : b.has('deno.jsonc') ? 'deno.jsonc' : null;
  if (!file) return;
  b.lang('typescript');
  b.pm('deno', file);
  const text = b.text(file);
  const tasks = jget(text === null ? null : parseJson(text), 'tasks');
  for (const kind of STACK_COMMAND_KINDS) {
    for (const name of SCRIPT_NAMES[kind]) {
      const raw = jget(tasks, name);
      const body = typeof raw === 'string' ? raw : jstr(raw, 'command');
      if (body === null) continue;
      declared(b, kind, `deno task ${name}`, `${file} tasks.${name}`, body);
      break;
    }
  }
  conventions(b, file, { install: 'deno install', test: 'deno test', lint: 'deno lint', format: 'deno fmt --check' });
}

function rustEcosystem(b: Builder): void {
  if (!b.has('Cargo.toml')) return;
  b.lang('rust');
  b.pm('cargo', b.has('Cargo.lock') ? 'Cargo.lock' : 'Cargo.toml');
  const text = b.text('Cargo.toml');
  const doc = text === null ? null : parseToml(text);
  if (doc && tomlHas(doc, 'workspace')) b.monorepoCandidate ??= { tool: 'cargo workspace', evidence: 'Cargo.toml [workspace]' };
  if (doc) b.tool('rust', tomlString(doc, 'package', 'rust-version'), 'Cargo.toml package.rust-version');
  const tc = b.text('rust-toolchain.toml');
  if (tc !== null) b.tool('rust', tomlString(parseToml(tc), 'toolchain', 'channel'), 'rust-toolchain.toml');
  b.tool('rust', firstLine(b.text('rust-toolchain')), 'rust-toolchain');
  if (doc) {
    const deps = tomlText(doc, 'dependencies') + '\n' + tomlText(doc, 'workspace.dependencies');
    for (const [dep, fw] of [['axum', 'axum'], ['actix-web', 'actix-web'], ['rocket', 'rocket'], ['tauri', 'tauri']] as const) if (mentions(deps, dep)) b.framework(fw);
  }
  conventions(b, 'Cargo.toml', { install: 'cargo fetch', build: 'cargo build', test: 'cargo test', lint: 'cargo clippy', typecheck: 'cargo check', format: 'cargo fmt --check' });
}

function goEcosystem(b: Builder): void {
  if (!b.has('go.mod')) return;
  b.lang('go');
  b.pm('go', 'go.mod');
  const text = b.text('go.mod') ?? '';
  b.tool('go', /^go\s+(\S+)\s*$/m.exec(text)?.[1], 'go.mod go');
  b.tool('go', /^toolchain\s+go(\S+)\s*$/m.exec(text)?.[1], 'go.mod toolchain');
  for (const [mod, fw] of [['github.com/gin-gonic/gin', 'gin'], ['github.com/labstack/echo', 'echo'], ['github.com/gofiber/fiber', 'fiber']] as const) if (text.includes(mod)) b.framework(fw);
  conventions(b, 'go.mod', { install: 'go mod download', build: 'go build ./...', test: 'go test ./...', lint: 'go vet ./...', format: 'gofmt -l .' });
}

function pythonEcosystem(b: Builder): void {
  const pyText = b.text('pyproject.toml');
  const has = (f: string) => b.has(f);
  if (!has('pyproject.toml') && !has('requirements.txt') && !has('Pipfile') && !has('setup.py') && !has('setup.cfg')) return;
  b.lang('python');
  const doc: TomlDoc | null = pyText === null ? null : parseToml(pyText);
  const tool = (s: string) => doc !== null && tomlHas(doc, `tool.${s}`);
  const found: [string, string][] = [];
  if (has('uv.lock')) found.push(['uv', 'uv.lock']);
  else if (tool('uv')) found.push(['uv', 'pyproject.toml [tool.uv]']);
  if (has('poetry.lock')) found.push(['poetry', 'poetry.lock']);
  else if (tool('poetry')) found.push(['poetry', 'pyproject.toml [tool.poetry]']);
  if (has('pdm.lock')) found.push(['pdm', 'pdm.lock']);
  else if (tool('pdm')) found.push(['pdm', 'pyproject.toml [tool.pdm]']);
  if (tool('hatch') || has('hatch.toml')) found.push(['hatch', has('hatch.toml') ? 'hatch.toml' : 'pyproject.toml [tool.hatch]']);
  if (has('Pipfile')) found.push(['pipenv', has('Pipfile.lock') ? 'Pipfile.lock' : 'Pipfile']);
  if (found.length === 0) found.push(['pip', has('requirements.txt') ? 'requirements.txt' : has('pyproject.toml') ? 'pyproject.toml' : has('setup.py') ? 'setup.py' : 'setup.cfg']);
  for (const [id, ev] of found) b.pm(id, ev);
  const primary = found[0][0];

  b.tool('python', firstLine(b.text('.python-version')), '.python-version');
  if (doc) b.tool('python', tomlString(doc, 'project', 'requires-python'), 'pyproject.toml project.requires-python');
  if (doc) b.tool('python', tomlString(doc, 'tool.poetry.dependencies', 'python'), 'pyproject.toml tool.poetry.dependencies.python');

  const depText = [doc ? tomlText(doc, 'project') : '', doc ? tomlText(doc, 'tool.poetry') : '', doc ? tomlText(doc, 'dependency-groups') : '', b.text('requirements.txt') ?? '', b.text('Pipfile') ?? ''].join('\n');
  for (const fw of PYTHON_FRAMEWORKS) if (mentions(depText, fw)) b.framework(fw);
  const ruff = tool('ruff') || has('ruff.toml') || has('.ruff.toml') || mentions(depText, 'ruff');
  const mypy = tool('mypy') || has('mypy.ini') || mentions(depText, 'mypy');
  const black = tool('black');

  const run = (c: string) => (primary === 'pip' ? c : primary === 'hatch' ? `hatch run ${c}` : `${primary} run ${c}`);
  const evidence = found[0][1];
  conventions(b, evidence, {
    install:
      primary === 'uv'
        ? 'uv sync'
        : primary === 'poetry'
          ? 'poetry install'
          : primary === 'pdm'
            ? 'pdm install'
            : primary === 'hatch'
              ? 'hatch env create'
              : primary === 'pipenv'
                ? 'pipenv install --dev'
                : has('requirements.txt')
                  ? 'pip install -r requirements.txt'
                  : 'pip install -e .',
    test: primary === 'hatch' ? 'hatch test' : primary === 'pip' ? 'python -m pytest' : run('pytest'),
    lint: ruff && run('ruff check .'),
    typecheck: mypy && run('mypy .'),
    format: ruff ? run('ruff format --check .') : black && run('black --check .'),
  });
}

function rubyEcosystem(b: Builder): void {
  if (!b.has('Gemfile')) return;
  b.lang('ruby');
  b.pm('bundler', b.has('Gemfile.lock') ? 'Gemfile.lock' : 'Gemfile');
  const gemfile = b.text('Gemfile') ?? '';
  b.tool('ruby', firstLine(b.text('.ruby-version')), '.ruby-version');
  b.tool('ruby', /^\s*ruby\s+["']([^"']+)["']/m.exec(gemfile)?.[1], 'Gemfile ruby');
  const gem = (name: string) => new RegExp(`^\\s*gem\\s+["']${name}["']`, 'm').test(gemfile);
  if (gem('rails')) b.framework('rails');
  if (gem('sinatra')) b.framework('sinatra');
  const rspec = gem('rspec') || gem('rspec-rails') || b.has('.rspec');
  conventions(b, 'Gemfile', {
    install: 'bundle install',
    test: rspec ? 'bundle exec rspec' : gem('rails') ? 'bin/rails test' : 'bundle exec rake test',
    lint: gem('rubocop') && 'bundle exec rubocop',
  });
}

function phpEcosystem(b: Builder): void {
  if (!b.has('composer.json')) return;
  b.lang('php');
  b.pm('composer', b.has('composer.lock') ? 'composer.lock' : 'composer.json');
  const text = b.text('composer.json');
  const doc = text === null ? null : parseJson(text);
  b.tool('php', jstr(jget(doc, 'require'), 'php'), 'composer.json require.php');
  const deps = [...jkeys(jget(doc, 'require')), ...jkeys(jget(doc, 'require-dev'))];
  if (deps.includes('laravel/framework')) b.framework('laravel');
  if (deps.includes('symfony/framework-bundle')) b.framework('symfony');
  const scripts = jget(doc, 'scripts');
  for (const kind of STACK_COMMAND_KINDS) {
    for (const name of SCRIPT_NAMES[kind]) {
      const raw = jget(scripts, name);
      const body = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.filter((x) => typeof x === 'string').join('\n') : null;
      if (body === null) continue;
      declared(b, kind, name === 'test' ? 'composer test' : `composer run-script ${name}`, `composer.json scripts.${name}`, body);
      break;
    }
  }
  const pest = deps.includes('pestphp/pest');
  const phpunit = deps.includes('phpunit/phpunit') || b.has('phpunit.xml') || b.has('phpunit.xml.dist');
  conventions(b, 'composer.json', { install: 'composer install', test: pest ? 'vendor/bin/pest' : phpunit && 'vendor/bin/phpunit' });
}

function jvmEcosystem(b: Builder): void {
  const javaVersion = firstLine(b.text('.java-version'));
  if (b.has('pom.xml')) {
    const pom = b.text('pom.xml') ?? '';
    b.lang(pom.includes('kotlin-maven-plugin') ? 'kotlin' : 'java');
    b.pm('maven', 'pom.xml');
    const v = /<maven\.compiler\.release>\s*([^<\s]+)\s*</.exec(pom)?.[1] ?? /<java\.version>\s*([^<\s]+)\s*</.exec(pom)?.[1] ?? /<maven\.compiler\.source>\s*([^<\s]+)\s*</.exec(pom)?.[1];
    b.tool('java', v, 'pom.xml');
    if (pom.includes('spring-boot')) b.framework('spring');
    const mvn = b.has('mvnw') ? './mvnw' : 'mvn';
    conventions(b, b.has('mvnw') ? 'mvnw' : 'pom.xml', { install: `${mvn} -B dependency:resolve`, build: `${mvn} -B package -DskipTests`, test: `${mvn} -B verify` });
  }
  const gradleFile = ['build.gradle.kts', 'build.gradle', 'settings.gradle.kts', 'settings.gradle'].find((f) => b.has(f));
  if (gradleFile) {
    const g = b.text('build.gradle.kts') ?? b.text('build.gradle') ?? '';
    b.lang(/org\.jetbrains\.kotlin|kotlin\(/.test(g) ? 'kotlin' : 'java');
    b.pm('gradle', gradleFile);
    b.tool('java', /JavaLanguageVersion\.of\(\s*(\d+)\s*\)/.exec(g)?.[1] ?? /JavaVersion\.VERSION_(\d+(?:_\d+)?)/.exec(g)?.[1]?.replace(/_/g, '.'), gradleFile);
    if (g.includes('org.springframework.boot')) b.framework('spring');
    if (g.includes('com.android.application') || g.includes('com.android.library')) b.framework('android');
    const gr = b.has('gradlew') ? './gradlew' : 'gradle';
    conventions(b, b.has('gradlew') ? 'gradlew' : gradleFile, { build: `${gr} assemble`, test: `${gr} test` });
  }
  if (b.has('pom.xml') || gradleFile) b.tool('java', javaVersion, '.java-version');
}

function dotnetEcosystem(b: Builder): void {
  const sln = b.rootMatching(/\.sln$/);
  const cs = b.rootMatching(/\.csproj$/);
  const fs = b.rootMatching(/\.fsproj$/);
  if (sln.length + cs.length + fs.length === 0 && !b.has('global.json')) return;
  if (cs.length || (!fs.length && (sln.length || b.has('global.json')))) b.lang('csharp');
  if (fs.length) b.lang('fsharp');
  const evidence = sln[0] ?? cs[0] ?? fs[0] ?? 'global.json';
  b.pm('dotnet', evidence);
  const g = b.text('global.json');
  b.tool('dotnet', jstr(jget(g === null ? null : parseJson(g), 'sdk'), 'version'), 'global.json sdk.version');
  for (const p of [...cs, ...fs]) if (/Sdk="Microsoft\.NET\.Sdk\.Web"/.test(b.text(p) ?? '')) b.framework('aspnetcore');
  conventions(b, evidence, { install: 'dotnet restore', build: 'dotnet build', test: 'dotnet test', format: 'dotnet format --verify-no-changes' });
}

function elixirEcosystem(b: Builder): void {
  if (!b.has('mix.exs')) return;
  b.lang('elixir');
  b.pm('mix', b.has('mix.lock') ? 'mix.lock' : 'mix.exs');
  const mix = b.text('mix.exs') ?? '';
  b.tool('elixir', /elixir:\s*"([^"]+)"/.exec(mix)?.[1], 'mix.exs elixir');
  if (/\{\s*:phoenix\s*,/.test(mix)) b.framework('phoenix');
  conventions(b, 'mix.exs', {
    install: 'mix deps.get',
    build: 'mix compile',
    test: 'mix test',
    lint: /\{\s*:credo\s*,/.test(mix) && 'mix credo',
    format: 'mix format --check-formatted',
  });
}

function dartEcosystem(b: Builder): void {
  if (!b.has('pubspec.yaml')) return;
  b.lang('dart');
  b.pm('pub', b.has('pubspec.lock') ? 'pubspec.lock' : 'pubspec.yaml');
  const doc = parseYaml(b.text('pubspec.yaml') ?? '');
  b.tool('dart', ystr(yget(yget(doc, 'environment'), 'sdk')), 'pubspec.yaml environment.sdk');
  const flutter = yget(yget(doc, 'dependencies'), 'flutter') !== undefined;
  if (flutter) b.framework('flutter');
  const t = flutter ? 'flutter' : 'dart';
  conventions(b, 'pubspec.yaml', { install: `${t} pub get`, test: `${t} test`, lint: `${t} analyze`, format: 'dart format --output=none --set-exit-if-changed .' });
}

function swiftEcosystem(b: Builder): void {
  if (!b.has('Package.swift')) return;
  b.lang('swift');
  b.pm('swiftpm', b.has('Package.resolved') ? 'Package.resolved' : 'Package.swift');
  b.tool('swift', /^\/\/\s*swift-tools-version:\s*([0-9][0-9.]*)/m.exec(b.text('Package.swift') ?? '')?.[1], 'Package.swift swift-tools-version');
  conventions(b, 'Package.swift', { install: 'swift package resolve', build: 'swift build', test: 'swift test' });
}

function cEcosystem(b: Builder): void {
  const cmake = b.has('CMakeLists.txt');
  const cFiles = b.rootMatching(/\.c$/);
  const cppFiles = b.rootMatching(/\.(cpp|cc|cxx|hpp)$/);
  if (cmake) {
    const text = b.text('CMakeLists.txt') ?? '';
    const project = /project\s*\(([^)]*)\)/i.exec(text)?.[1] ?? '';
    const langs = /LANGUAGES\s+([A-Za-z\s]+)/i.exec(project)?.[1] ?? project.split(/\s+/).slice(1).join(' ');
    const c = /\bC\b/.test(langs);
    const cxx = /\bCXX\b/.test(langs);
    if (c && !cxx) b.lang('c');
    else b.lang('cpp');
    if (c && cxx) b.lang('c');
    b.tool('cmake', /cmake_minimum_required\s*\(\s*VERSION\s+([0-9][0-9.]*)/i.exec(text)?.[1], 'CMakeLists.txt cmake_minimum_required');
    conventions(b, 'CMakeLists.txt', { build: 'cmake -S . -B build && cmake --build build', test: 'ctest --test-dir build' });
  }
  if (cFiles.length) b.lang('c');
  if (cppFiles.length) b.lang('cpp');
}

function toolVersions(b: Builder): void {
  const text = b.text('.tool-versions');
  if (text === null) return;
  const names: Record<string, string> = { nodejs: 'node', golang: 'go', ruby: 'ruby', python: 'python', rust: 'rust', java: 'java', elixir: 'elixir', erlang: 'erlang', dotnet: 'dotnet', 'dotnet-core': 'dotnet', pnpm: 'pnpm', yarn: 'yarn', bun: 'bun', deno: 'deno', dart: 'dart', flutter: 'flutter', php: 'php' };
  for (const line of text.split(/\r?\n/).slice(0, 100)) {
    const m = /^\s*([a-z0-9_-]+)\s+(\S+)/.exec(line);
    if (m && Object.prototype.hasOwnProperty.call(names, m[1])) b.tool(names[m[1]], m[2], '.tool-versions');
  }
}

function deployHints(b: Builder): void {
  for (const [path, target] of DEPLOY_HINTS) {
    if (!b.has(path) || b.profile.deployHints.some((d) => d.target === target)) continue;
    b.profile.deployHints.push({ target, evidence: path });
  }
}

function monorepo(b: Builder): void {
  for (const [path, tool] of MONOREPO_FILES) {
    if (b.has(path)) {
      b.monorepo(tool, path);
      return;
    }
  }
  if (b.monorepoCandidate) b.monorepo(b.monorepoCandidate.tool, b.monorepoCandidate.evidence);
}

// ---------------------------------------------------------------------------
// CI files
// ---------------------------------------------------------------------------

/** One command a CI step runs, with where it was found. */
interface CiStep {
  text: string;
  evidence: string;
}

const GITLAB_RESERVED = new Set(['stages', 'variables', 'default', 'include', 'workflow', 'image', 'services', 'before_script', 'after_script', 'cache', 'spec']);

function q(s: string): string {
  const v = stackVisibleText(s);
  return `'${v.length > 80 ? `${v.slice(0, 77)}...` : v}'`;
}

function githubActions(b: Builder, steps: CiStep[]): CiDetection | null {
  const files = b.under('.github/workflows', /^[^/]+\.ya?ml$/).slice(0, STACK_MAX_CI_FILES);
  if (files.length === 0) return null;
  const pipelines: string[] = [];
  const prJobs: string[] = [];
  for (const file of files) {
    const text = b.text(file);
    if (text === null) continue;
    const doc = parseYaml(text);
    const name = label(ystr(yget(doc, 'name')) ?? file.slice(file.lastIndexOf('/') + 1));
    if (!pipelines.includes(name)) pipelines.push(name);
    const triggers = ylist(yget(doc, 'on') ?? yget(doc, 'true'));
    const onPr = triggers.some((t) => t === 'pull_request' || t === 'pull_request_target' || t === 'merge_group');
    const jobs = yget(doc, 'jobs');
    if (!isYamlMap(jobs)) continue;
    for (const [id, job] of Object.entries(jobs)) {
      const jobName = label(ystr(yget(job, 'name')) ?? id);
      if (onPr && !prJobs.includes(jobName)) prJobs.push(jobName);
      const list = yget(job, 'steps');
      if (!Array.isArray(list)) continue;
      list.forEach((step, i) => {
        const run = ystr(yget(step, 'run'));
        if (run === null) return;
        const label = ystr(yget(step, 'name')) ?? `${i + 1}`;
        steps.push({ text: run, evidence: `${file} job ${q(jobName)} step ${q(label)}` });
      });
    }
  }
  return { provider: 'github-actions', files, pipelines, ...(prJobs.length ? { pullRequestJobs: prJobs } : {}) };
}

function gitlabCi(b: Builder, steps: CiStep[]): CiDetection | null {
  const file = '.gitlab-ci.yml';
  if (!b.has(file)) return null;
  const doc = parseYaml(b.text(file) ?? '');
  const workflow = yget(doc, 'workflow');
  const pipelineName = ystr(yget(workflow, 'name'));
  const mr = (v: YamlValue | undefined) => v !== undefined && /merge_request/.test(JSON.stringify(v));
  const workflowMr = mr(yget(workflow, 'rules'));
  const prJobs: string[] = [];
  if (isYamlMap(doc)) {
    for (const [name, job] of Object.entries(doc)) {
      if (GITLAB_RESERVED.has(name) || name.startsWith('.') || !isYamlMap(job)) continue;
      const rules = yget(job, 'rules') ?? yget(job, 'only');
      if (mr(rules) || (workflowMr && rules === undefined)) prJobs.push(label(name));
      for (const line of ylist(yget(job, 'script'))) steps.push({ text: line, evidence: `${file} job ${q(name)}` });
    }
  }
  return { provider: 'gitlab-ci', files: [file], pipelines: pipelineName ? [label(pipelineName)] : [], ...(prJobs.length ? { pullRequestJobs: prJobs } : {}) };
}

/** Walk a parsed document for step commands under these keys (Azure `script`, `bash`, `pwsh`). */
function walkSteps(v: YamlValue | undefined, keys: readonly string[], file: string, steps: CiStep[], depth = 0): void {
  if (depth > 12 || v === undefined || v === null || typeof v === 'string') return;
  if (Array.isArray(v)) {
    for (const x of v) walkSteps(x, keys, file, steps, depth + 1);
    return;
  }
  for (const k of keys) {
    const s = ystr(yget(v, k));
    if (s !== null) {
      const label = ystr(yget(v, 'displayName')) ?? ystr(yget(v, 'name')) ?? k;
      steps.push({ text: s, evidence: `${file} step ${q(label)}` });
    }
  }
  for (const [k, x] of Object.entries(v)) if (!keys.includes(k)) walkSteps(x, keys, file, steps, depth + 1);
}

function azurePipelines(b: Builder, steps: CiStep[]): CiDetection | null {
  const file = ['azure-pipelines.yml', 'azure-pipelines.yaml'].find((f) => b.has(f));
  if (!file) return null;
  const doc = parseYaml(b.text(file) ?? '');
  walkSteps(doc, ['script', 'bash', 'pwsh', 'powershell'], file, steps);
  const name = ystr(yget(doc, 'name'));
  return { provider: 'azure-pipelines', files: [file], pipelines: name ? [label(name)] : [] };
}

function jenkins(b: Builder, steps: CiStep[]): CiDetection | null {
  if (!b.has('Jenkinsfile')) return null;
  const text = b.text('Jenkinsfile') ?? '';
  for (const line of text.split(/\r?\n/).slice(0, 4000)) {
    if (line.length > 1000) continue;
    const m = /\b(?:sh|bat)\s*\(?\s*(?:script:\s*)?(['"])([^'"]+)\1/.exec(line);
    if (m) steps.push({ text: m[2], evidence: 'Jenkinsfile' });
  }
  return { provider: 'jenkins', files: ['Jenkinsfile'], pipelines: [] };
}

function circleci(b: Builder, steps: CiStep[]): CiDetection | null {
  const file = ['.circleci/config.yml', '.circleci/config.yaml'].find((f) => b.has(f));
  if (!file) return null;
  const doc = parseYaml(b.text(file) ?? '');
  const workflows = yget(doc, 'workflows');
  const pipelines = isYamlMap(workflows) ? Object.keys(workflows).filter((k) => k !== 'version').map(label) : [];
  const jobs = yget(doc, 'jobs');
  if (isYamlMap(jobs)) {
    for (const [id, job] of Object.entries(jobs)) {
      const list = yget(job, 'steps');
      if (!Array.isArray(list)) continue;
      for (const step of list) {
        const run = yget(step, 'run');
        const text = ystr(run) ?? ystr(yget(run, 'command'));
        if (text !== null) steps.push({ text, evidence: `${file} job ${q(id)} step ${q(ystr(yget(run, 'name')) ?? 'run')}` });
      }
    }
  }
  return { provider: 'circleci', files: [file], pipelines };
}

function buildkite(b: Builder, steps: CiStep[]): CiDetection | null {
  const file = ['.buildkite/pipeline.yml', '.buildkite/pipeline.yaml'].find((f) => b.has(f));
  if (!file) return null;
  const doc = parseYaml(b.text(file) ?? '');
  const list = yget(doc, 'steps') ?? (Array.isArray(doc) ? doc : undefined);
  if (Array.isArray(list)) {
    for (const step of list) {
      const label = ystr(yget(step, 'label')) ?? ystr(yget(step, 'key')) ?? 'step';
      for (const text of [...ylist(yget(step, 'command')), ...ylist(yget(step, 'commands'))]) steps.push({ text, evidence: `${file} step ${q(label)}` });
    }
  }
  return { provider: 'buildkite', files: [file], pipelines: [] };
}

const CI_DETECTORS: readonly (readonly [CiProviderId, (b: Builder, steps: CiStep[]) => CiDetection | null])[] = [
  ['github-actions', githubActions],
  ['gitlab-ci', gitlabCi],
  ['azure-pipelines', azurePipelines],
  ['jenkins', jenkins],
  ['circleci', circleci],
  ['buildkite', buildkite],
];

function ciFiles(b: Builder): void {
  const steps: CiStep[] = [];
  for (const [, detect] of CI_DETECTORS) {
    const d = detect(b, steps);
    if (d) b.profile.ci.push(d);
  }
  for (const step of steps.slice(0, 500)) {
    for (const { command, kind } of ciStepCommands(step.text)) b.offer(kind, { command, confidence: 'ci', evidence: step.evidence });
  }
}

/**
 * Which CI providers a repo uses, from its file list and (when given) the CI files' text.
 * Pure; the CI provider implementations in packages/integrations can call it for their detect().
 */
export function detectCiFiles(input: StackInput): CiDetection[] {
  const b = new Builder(input);
  ciFiles(b);
  return b.profile.ci;
}

// ---------------------------------------------------------------------------
// detectStack
// ---------------------------------------------------------------------------

/**
 * Detect a repo's stack. Pure: no I/O, never runs anything it reads, and the same input always
 * gives the same profile (lists follow the rules tables' order, not the input's).
 */
export function detectStack(input: StackInput): StackProfile {
  const b = new Builder(input);
  // Order is rank: task runners first (the repo's own entry point), then ecosystems, then CI.
  taskRunners(b);
  nodeEcosystem(b);
  denoEcosystem(b);
  rustEcosystem(b);
  goEcosystem(b);
  pythonEcosystem(b);
  rubyEcosystem(b);
  phpEcosystem(b);
  jvmEcosystem(b);
  dotnetEcosystem(b);
  elixirEcosystem(b);
  dartEcosystem(b);
  swiftEcosystem(b);
  cEcosystem(b);
  toolVersions(b);
  ciFiles(b);
  monorepo(b);
  deployHints(b);
  return b.finish();
}

/** A short, stable fingerprint of a profile (FNV-1a over its JSON), for "plan based on" checks. */
export function stackProfileHash(p: StackProfile): string {
  const s = JSON.stringify(p);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

// ---------------------------------------------------------------------------
// The agent module's messages
// ---------------------------------------------------------------------------

/**
 * Agent module `stack` (ext 'stack'):
 *
 *   agent -> hub  { type: 'report', data: StackReport }          after each detection, and in answer to a request
 *   hub -> agent  { type: 'request', id?, data: StackRequest }   resend the latest reports (a project's, or all)
 */
export const STACK_EXT = 'stack';

export interface StackRequest {
  projectId?: Id;
}

/** What the agent module `stack` reports for a project (ext 'stack', type 'report'). */
export interface StackReport {
  projectId: Id;
  /** The main checkout's HEAD the profile was read at. */
  head: string;
  at: Ts;
  profile: StackProfile;
}

/** The fewest milliseconds between two timed detections of one project on a machine (it also runs on HEAD change). */
export const STACK_DETECT_MIN_INTERVAL_MS = 60 * 60_000;

/** The fewest milliseconds between two detections of one project, whatever caused them (a rebase moves HEAD many times). */
export const STACK_DETECT_MIN_GAP_MS = 60_000;

/** One machine's latest report for a project, as the hub keeps it. */
export interface StackEntry extends StackReport {
  /** The machine that read it: always the connection's, never anything in the report. */
  machineId: Id;
  /** When the hub took the report. */
  receivedAt: Ts;
}

/**
 * state.ext.stack and GET /api/stack. Only reports of machines that are online and hold a
 * checkout of the project count: a machine's reports go when it disconnects or is removed.
 */
export interface StackState {
  /** Per project, the newest report (by `at`, then machine id) among those machines. */
  profiles: Record<Id, StackEntry>;
  /** Every counted report, one per machine and project: by project id, then newest first. */
  reports: StackEntry[];
}

/** POST /api/stack/refresh: ask machines to resend their latest reports (a project's, or all). */
export interface StackRefreshRequest {
  projectId?: Id;
}

export interface StackRefreshResult {
  /** How many online machines were asked. */
  sent: number;
}

// ---------------------------------------------------------------------------
// What a hub keeps of a report (a node is untrusted)
// ---------------------------------------------------------------------------

/** Caps on a report a hub keeps. A text a person reads is clipped; a command, path or version is dropped, never clipped. */
export const STACK_LIMITS = {
  /** Languages, frameworks, package managers, toolchains, deploy hints, unknowns, CI providers. */
  list: 32,
  /** Files, pipelines and pull-request jobs of one CI provider. */
  ciList: 64,
  /** A language, framework, tool, package manager or deploy target id. */
  id: 40,
  evidence: 600,
  version: 80,
  command: 1000,
  /** What a declared command runs: over this the command is dropped, since part of it would hide the rest. */
  runs: 32 * 1024,
  path: 300,
  name: 200,
  unknown: 600,
  projectId: 200,
  head: 64,
  /** A report whose kept form is larger than this is dropped whole. */
  reportBytes: 512 * 1024,
  /** Reports a hub keeps per machine (one per project). */
  projectsPerMachine: 500,
} as const;

const STACK_ID_RE = /^[a-z0-9][a-z0-9.+#_-]*$/;
const STACK_HEAD_RE = /^[0-9a-f]{4,64}$/i;

const isRec = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A lower-case id from the rules tables ('pnpm', 'cloudflare-workers'); null for anything else. */
function stackId(v: unknown): string | null {
  return typeof v === 'string' && v.length <= STACK_LIMITS.id && STACK_ID_RE.test(v) ? v : null;
}

/** Display text: invisible characters escaped, clipped with an ellipsis. Null when blank. */
function stackClip(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null;
  const s = stackVisibleText(v);
  if (!s.trim()) return null;
  return s.length > max ? `${s.slice(0, max - 3)}...` : s;
}

/** Text that must be whole (a command, a path, a version): invisible characters escaped; null when blank or over max. */
function stackExact(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null;
  const s = stackVisibleText(v);
  return s.trim() && s.length <= max ? s : null;
}

/** At most `max` kept items, the first of each key; reads at most 4 x max raw items. */
function stackList<T>(v: unknown, max: number, each: (x: unknown) => T | null, key: (x: T) => string): T[] {
  if (!Array.isArray(v)) return [];
  const out: T[] = [];
  const seen = new Set<string>();
  for (const x of v.slice(0, max * 4)) {
    if (out.length >= max) break;
    const y = each(x);
    if (y === null) continue;
    const k = key(y);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(y);
  }
  return out;
}

function stackEvidenceOf(v: unknown, idKey: 'id' | 'tool' | 'target'): { id: string; evidence: string } | null {
  if (!isRec(v)) return null;
  const id = stackId(v[idKey]);
  const evidence = stackClip(v.evidence, STACK_LIMITS.evidence);
  return id && evidence ? { id, evidence } : null;
}

function stackCommandOf(v: unknown): StackCommand | null {
  if (!isRec(v)) return null;
  const command = stackExact(v.command, STACK_LIMITS.command);
  const confidence = STACK_CONFIDENCE_ORDER.find((c) => c === v.confidence);
  const evidence = stackClip(v.evidence, STACK_LIMITS.evidence);
  if (!command || !confidence || !evidence) return null;
  if (v.runs === undefined || v.runs === null) return { command, confidence, evidence };
  const runs = stackExact(v.runs, STACK_LIMITS.runs);
  return runs ? { command, confidence, evidence, runs } : null;
}

function stackCiOf(v: unknown): CiDetection | null {
  if (!isRec(v)) return null;
  const provider = CI_PROVIDER_IDS.find((p) => p === v.provider);
  if (!provider) return null;
  const path = (x: unknown) => stackExact(x, STACK_LIMITS.path);
  const name = (x: unknown) => stackClip(x, STACK_LIMITS.name);
  const out: CiDetection = { provider, files: stackList(v.files, STACK_LIMITS.ciList, path, String), pipelines: stackList(v.pipelines, STACK_LIMITS.ciList, name, String) };
  if (Array.isArray(v.pullRequestJobs)) out.pullRequestJobs = stackList(v.pullRequestJobs, STACK_LIMITS.ciList, name, String);
  return out;
}

/**
 * What a hub keeps of a profile a machine sent: known fields only, ids in the rules tables'
 * alphabet, every repo-given text with invisible characters escaped (an agent that skipped
 * detection's own escaping changes nothing), lists capped, display texts clipped, and commands,
 * paths and versions dropped whole when they do not fit. A profile detectStack made comes back
 * equal.
 */
export function sanitizeStackProfile(v: unknown): StackProfile {
  const p = isRec(v) ? v : {};
  const max = STACK_LIMITS.list;
  const out = emptyStackProfile();
  out.languages = stackList(p.languages, max, stackId, String);
  out.frameworks = stackList(p.frameworks, max, stackId, String);
  out.packageManagers = stackList(p.packageManagers, max, (x) => stackEvidenceOf(x, 'id'), (x) => x.id);
  const mono = stackEvidenceOf(p.monorepo, 'tool');
  if (mono) out.monorepo = { tool: mono.id, evidence: mono.evidence };
  out.toolchains = stackList(
    p.toolchains,
    max,
    (x) => {
      const t = stackEvidenceOf(x, 'tool');
      const version = isRec(x) ? stackExact(x.version, STACK_LIMITS.version) : null;
      return t && version ? { tool: t.id, version, evidence: t.evidence } : null;
    },
    (x) => `${x.tool} ${x.version}`,
  );
  if (isRec(p.commands)) {
    for (const kind of STACK_COMMAND_KINDS) {
      if (!Object.prototype.hasOwnProperty.call(p.commands, kind)) continue;
      const c = stackCommandOf(p.commands[kind]);
      if (c) out.commands[kind] = c;
    }
  }
  out.ci = stackList(p.ci, max, stackCiOf, (x) => x.provider);
  out.deployHints = stackList(
    p.deployHints,
    max,
    (x) => {
      const d = stackEvidenceOf(x, 'target');
      return d ? { target: d.id, evidence: d.evidence } : null;
    },
    (x) => x.target,
  );
  out.unknowns = stackList(p.unknowns, max, (x) => stackClip(x, STACK_LIMITS.unknown), String);
  const verify = stackCommandOf(p.verify);
  if (verify) out.verify = verify;
  return out;
}

/**
 * The verify command Landing settings suggest, with Use this (it only fills the form): the repo's
 * install, then its test:affected script, else its test command. Install comes first as in
 * detectedVerifyCommand: the queue verifies in a fresh checkout. `evidence` and `runs` are the test
 * step's. Null when the repo declares no test.
 */
export function suggestedVerifyCommand(p: StackProfile | null | undefined): { command: string; evidence: string; runs?: string } | null {
  const test = p?.verify ?? p?.commands.test;
  if (!test?.command.trim()) return null;
  const command = [p?.commands.install?.command.trim(), test.command.trim()].filter(Boolean).join(' && ');
  return test.runs ? { command, evidence: test.evidence, runs: test.runs } : { command, evidence: test.evidence };
}

/**
 * What a hub keeps of a `report` frame (ext 'stack'), or null when it is not one: a project id
 * without invisible characters, a hex HEAD, a time at most a minute ahead of the hub's clock (0
 * or missing: now), and a sanitized profile. Null too when the kept report is over
 * STACK_LIMITS.reportBytes.
 */
export function sanitizeStackReport(v: unknown, now: number): StackReport | null {
  if (!isRec(v)) return null;
  const rawId = typeof v.projectId === 'string' ? v.projectId.trim() : '';
  const projectId = rawId.length <= STACK_LIMITS.projectId && !INVISIBLE.test(rawId) ? rawId : '';
  const head = typeof v.head === 'string' && v.head.length <= STACK_LIMITS.head && STACK_HEAD_RE.test(v.head) ? v.head.toLowerCase() : '';
  if (!projectId || !head) return null;
  const at = typeof v.at === 'number' && Number.isFinite(v.at) && v.at > 0 ? Math.min(Math.round(v.at), now + 60_000) : now;
  const report: StackReport = { projectId, head, at, profile: sanitizeStackProfile(v.profile) };
  return JSON.stringify(report).length <= STACK_LIMITS.reportBytes ? report : null;
}

/**
 * The verify command a landing queue uses when nobody set one (ADR 0066): the repository's own
 * install, typecheck and test commands, in that order, joined with &&. The queue verifies in a
 * fresh checkout, so install comes first. Null when no test command was found: then a person sets
 * one. Repo content, like every detected command: it runs only when a change is submitted to land,
 * which is when a person or agent asked for that project's tests to run.
 */
export function detectedVerifyCommand(profile: Pick<StackProfile, 'commands'> | Pick<StackProfile, 'commands' | 'packageManagers' | 'monorepo' | 'languages'> | null | undefined): { command: string; evidence: string } | null {
  const c = profile?.commands;
  if (!c?.test) return null;
  const steps = [c.install, c.typecheck].filter((s): s is StackCommand => !!s && !!s.command.trim());
  // ADR 0068: the tests a change can reach (or all, for a whole-suite change), when the runner says how.
  const scope = 'packageManagers' in profile! ? testScopeFor(profile) : null;
  const test = scope ? scopedTestLine(scope) : c.test.command.trim();
  const evidence = [...steps.map((s) => `${s.command.trim()} (${s.evidence})`), scope && scope.related ? `tests in scope: ${scope.evidence}` : `${c.test.command.trim()} (${c.test.evidence})`];
  return { command: [...steps.map((s) => s.command.trim()), test].join(' && '), evidence: evidence.join('; ') };
}
